import { Button, CircularProgress, Grid, Typography } from "@mui/material";

import { Form, type FormProps, TextField } from "../../../ui/atoms/Form";

// Component strings
const tDistrictName = "District Name";
const tCounty = "County";
const tState = "State";
const tStateCode = "State Code";
const tAddressLine1 = "Address Line 1";
const tAddressLine2 = "Address Line 2";
const tAddressLine3 = "Address Line 3";
const tPhoneNumber = "Phone Number";
const tWebsite = "Website";
const tRequired = "This field is required";
const tCreateDistrict = "Create District";

export interface FormValues {
  districtName: string;
  county: string;
  state: string;
  stateInitials: string;
  phoneNumber: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  addressLine3: string;
}

export type CreateDistrictTemplateProps = Pick<FormProps, "debug"> & {
  initialValues?: FormValues;
  onClose: () => void;
  onSubmit: (values: FormValues) => Promise<{ FORM_ERROR: string } | undefined>;
};

export const CreateDistrictTemplate = ({
  initialValues,
  onClose: _onClose,
  onSubmit,
  ...props
}: CreateDistrictTemplateProps) => {
  const validateRequired = (value?: string) => {
    return value ? undefined : tRequired;
  };

  return (
    <Form<FormValues>
      {...props}
      initialValues={initialValues}
      onSubmit={(values, _form) => {
        return onSubmit(values);
      }}
    >
      {function WithForm({ submitting, submitError, handleSubmit }) {
        return (
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                name="districtName"
                required
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tDistrictName,
                  margin: "normal",
                }}
                validate={validateRequired}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="county"
                required
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tCounty,
                  margin: "normal",
                }}
                validate={validateRequired}
              />
            </Grid>
            <Grid size={8}>
              <TextField
                name="state"
                required
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tState,
                  margin: "normal",
                }}
                validate={validateRequired}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                name="stateInitials"
                required
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tStateCode,
                  margin: "normal",
                }}
                validate={validateRequired}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="addressLine1"
                required
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tAddressLine1,
                  margin: "normal",
                }}
                validate={validateRequired}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="addressLine2"
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tAddressLine2,
                  margin: "normal",
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="addressLine3"
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tAddressLine3,
                  margin: "normal",
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="phoneNumber"
                textFieldProps={{
                  inputProps: { maxLength: 100 },
                  label: tPhoneNumber,
                  margin: "normal",
                }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                name="website"
                textFieldProps={{
                  label: tWebsite,
                  margin: "normal",
                }}
              />
            </Grid>
            <Grid size={12}>
              <Button
                color="primary"
                disabled={submitting}
                fullWidth
                onClick={(e) => handleSubmit(e)}
                type="button"
                variant="contained"
              >
                {submitting ? <CircularProgress size={24} /> : tCreateDistrict}
              </Button>
            </Grid>
            {submitError && (
              <Grid size={12}>
                <Typography color="error">{submitError}</Typography>
              </Grid>
            )}
          </Grid>
        );
      }}
    </Form>
  );
};
